import { Box, Grid, Stack } from "@mui/material"
import { LIVE_TREE } from "library/constants.mjs"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { navigate } from "lib/routes/navigate"
import { useParams } from "react-router-dom"
import { appRoute } from "routes/app/home.runtime"
import { useRootFacilities } from "routes/facilities/lib/use-root-facilities"
import { FacilitiesEntry } from "routes/facilities/facilities-entry"
import { HelpTag } from "routes/help/helpTag"
import { useTreeItem } from "routes/schedule/lib/useTreeItem"
import { FacilityTile } from "routes/schedule/my-schedules/my-group-types/facility/facility-tile"
import { BadgeLabel } from "routes/regime/plugins/regime-title/badge-label"
import { FacilityPageTile } from "slot-definitions"
import { RegimeLandingPanel, RegimeLandingTopPanel } from "routes/facilities/facilities-slot-definitions"
import { Bound } from "lib/@components/binding/Bound"
import { GroupTile } from "routes/schedule/my-schedules/my-group-types/facility/group-tile"
import { NoResultsContainer } from "lib/@components/no-results/no-results"
import { registerConditionalPageNavigation } from "lib/routes/register-conditional-page-navigation"
import { hasDemand } from "lib/authorization/has-demand"
import { HELPTAG_MAINTENANCE } from "routes/help/help-tag-constants"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { alert } from "lib/@dialogs/alert"
import { Else, If } from "lib/@components/switch"
import { ShareRegimeSelector } from "routes/facilities/share-regime-selector"
import { useShareBreadcrumbProps } from "routes/facilities/use-share-breadcrumb-props"
import { isInvalidSharedRegime } from "routes/facilities/invalid-shared-regime"
import { isTreeIndexForClient } from "routes/schedule/lib/getTreeIndex"
import { getCurrentClientId } from "minimals-template/components/contexts/NognitoContext"

appRoute.register("facilities/:id", <Facilities $lazy />)
appRoute.register(
    "facilities",
    //AD: FIXME revisit this ensure permissions correct
    <FacilitiesEntry $lazy />
)

registerConditionalPageNavigation(
    () => hasDemand("$sharing"),
    "/app/facilities",
    "Maintenance Regimes",
    "pajamas:issue-type-maintenance",
    {
        subPaths: true,
    }
)

function convertHeading(heading) {
    const isLive = heading.startsWith("Live:")
    const isReady = heading.startsWith("Ready:")
    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Box>{heading.replace(/[^:]*:/, "")}</Box>

            <BadgeLabel
                color={isLive ? "myStuff" : "secondary"}
                sx={{ zoom: 0.7, lineHeight: 0 }}
                badgeContent={isLive ? "Live" : isReady ? "Candidate" : "Draft"}
            />
        </Stack>
    )
}

export function Facilities() {
    const { id } = useParams()
    const tree = useTreeItem(id)
    const liveRoot = useTreeItem(LIVE_TREE)
    const facilities = useRootFacilities(tree)
    const loaded = tree?.isLoaded
    const [share] = useSearchParam("share", null)
    const shareCrumbs = useShareBreadcrumbProps(tree, id)
    invalidSharedRegimeAlert(tree, share, !!liveRoot)

    if (share && !tree && !liveRoot) return <LoadingScreen />
    if (tree === null || (!!tree && !loaded)) return <LoadingScreen />

    return facilities?.length > 0 ? (
        <>
            <HelpTag tags={[HELPTAG_MAINTENANCE]} />
            <PageBreadcrumbContainer
                title={tree.label}
                heading={convertHeading(tree.label)}
                crumb={share && shareCrumbs ? shareCrumbs.crumb : tree.label.replace(/[^:]*:/, "")}
                parentCrumbs={share && shareCrumbs ? shareCrumbs.parentCrumbs : []}
            >
                <Box flex={1} sx={{ overflowY: "scroll" }} p={1}>
                    <Bound regimeTree={tree} regimeId={id}>
                        <RegimeLandingTopPanel.Slot />
                    </Bound>
                    <If value={!!share}>
                        {/* Share links browse the regime with the in-app selector instead of colour tiles */}
                        <ShareRegimeSelector $lazy regimeId={id} />
                        <Else>
                            <Grid container spacing={2} sx={{ p: 2 }}>
                                {facilities.map((facility) => (
                                    <Grid
                                        key={facility.id}
                                        item
                                        xs={6}
                                        md={4}
                                        lg={3}
                                        sx={{ display: "flex", flexDirection: "column" }}
                                    >
                                        <Bound parentItem={facility}>
                                            <FacilityPageTile.Slot
                                                type={facility.data.type ?? "group"}
                                                onClick={go(facility)}
                                                facility={facility.data}
                                                parentItem={facility}
                                            />
                                        </Bound>
                                    </Grid>
                                ))}
                            </Grid>
                        </Else>
                    </If>
                    <Bound regimeTree={tree} regimeId={id}>
                        <RegimeLandingPanel.Slot />
                    </Bound>
                </Box>
            </PageBreadcrumbContainer>
        </>
    ) : (
        <NoResultsContainer message="No facilities to view" />
    )

    function go(facility) {
        return () => {
            navigate(`/app/facilities/facility/${facility.id}`)
        }
    }
}

export function invalidSharedRegimeAlert(tree, share, treesLoaded = true) {
    const alertMessage =
        "There are no longer any valid Maintenance Regimes available using this link. Please contact the person who supplied you with the link."

    if (!treesLoaded) return

    if (
        isInvalidSharedRegime(
            tree,
            share,
            location.search,
            location.pathname,
            isTreeIndexForClient(getCurrentClientId())
        )
    ) {
        alert(alertMessage, "Error", "error")
        navigate("/app/?share=clear")
    }
}

FacilityPageTile("group").plug(<GroupTile />)
FacilityPageTile("simple").plug(<GroupTile />)
FacilityPageTile("facility").plug(<FacilityTile />)
